import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose, bindActionCreators } from "redux";
import { withTranslation } from "react-i18next";
import {
  loadNotifications,
  readNotification,
  dismissNotification,
  dismissAllNotifications,
  markAllAsRead
} from "actions/actionCreators/notificationActions";
import withRouter from "components/__temporary/hoc/withRouter";
import { showError } from "actions/actionCreators/snackbarActions";
import { ContextUrlProvider } from "utils/notifier/ContextUrlProvider";
import memoize from "lodash.memoize";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Grow, ClickAwayListener, Tooltip, Badge } from "@mui/material";
import { Typography, LinearProgress } from "@totalsoft/rocket-ui";
import NotificationListComponent from "./NotificationListComponent";
import {
  NotificationAnchorContainer,
  NotificationLoaderContainer,
  NotificationPaper,
  NotificationPopper
} from "./NotificationStyles";

class NotificationContainer extends Component {
  state = {
    open: false,
    notifications: [],
    currentPage: 1,
    totalNotifications: 0
  };

  componentDidMount() {
    if (!this.props.notifications.isLoaded) {
      this.props.actions.loadNotifications(1);
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.notifications !== state.notifications) {
      return {
        notifications: props.notifications,
        totalNotifications: props.totalNotifications
      };
    }
    return null;
  }

  loadMoreNotifications = async () => {
    const { notifications, totalNotifications, currentPage } = this.state;

    if (notifications.length < totalNotifications) {
      await this.props.actions.loadNotifications(currentPage + 1);
      this.setState(currentState => {
        return {
          ...currentState,
          currentPage: currentState.currentPage + 1
        };
      });
    }
  };

  handleClick = () => {
    this.setState({ open: !this.state.open });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  handleMarkAsRead = memoize(notification => () => {
    this.setState({ open: false });

    if (notification.sourceContextName && notification.sourceContextKeys) {
      const contextProvider = new ContextUrlProvider(notification.sourceContextName);

      if (!contextProvider.context) {
        this.props.actions.showError(this.props.t("Notifier.ContextUrlNotFound"));
      } else {
        this.props.history.push(contextProvider.context.getUrl(notification.sourceContextKeys));
      }
    }

    if (!notification.isRead) this.props.actions.readNotification(notification);
  });

  handleDismiss = memoize(notification => () => {
    this.props.actions.dismissNotification(notification);
  });

  handleDismissAll = () => {
    this.handleClose();
    this.props.actions.dismissAllNotifications();
  };

  render() {
    const { pendingCommands, unreadNotifications, t } = this.props;
    const { notifications, open } = this.state;

    return (
      <>
        <NotificationAnchorContainer
          ref={node => {
            this.anchorEl = node;
          }}
          aria-label="Notifications"
          aria-haspopup="true"
          aria-owns={open ? "menu-list" : null}
          onClick={this.handleClick}
        >
          <Tooltip title={t("Tooltips.Notifications")}>
            <Badge badgeContent={unreadNotifications} color="secondary">
              <NotificationsIcon />
            </Badge>
          </Tooltip>
          <NotificationLoaderContainer>
            {pendingCommands > 0 && <LinearProgress color="primary" />}
          </NotificationLoaderContainer>
        </NotificationAnchorContainer>
        <NotificationPopper placement="bottom-start" anchorEl={this.anchorEl} open={open} transition>
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={this.handleClose}>
              <Grow {...TransitionProps} timeout={350}>
                <NotificationPaper>
                  {notifications.length === 0 ? (
                    <Typography p="1">{t("Notifier.NoNotifications")}</Typography>
                  ) : (
                    <NotificationListComponent
                      notifications={notifications}
                      handleDismiss={this.handleDismiss}
                      handleDismissAll={this.handleDismissAll}
                      handleMarkAsRead={this.handleMarkAsRead}
                      loadMoreNotifications={this.loadMoreNotifications}
                    />
                  )}
                </NotificationPaper>
              </Grow>
            </ClickAwayListener>
          )}
        </NotificationPopper>
      </>
    );
  }
}

NotificationContainer.propTypes = {
  notifications: PropTypes.array.isRequired,
  unreadNotifications: PropTypes.number.isRequired,
  totalNotifications: PropTypes.number,
  pendingCommands: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    notifications: state.notifications.data,
    unreadNotifications: state.notifications.unreadNotifications,
    totalNotifications: state.notifications.totalNotifications,
    pendingCommands: state.pendingAsyncCommands.length
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { loadNotifications, readNotification, dismissNotification, dismissAllNotifications, markAllAsRead, showError },
      dispatch
    ),
    dispatch
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(withRouter(NotificationContainer));
